<template>
  <Modal v-model="isDisplayed" v-if="level" :header="false" :footer="false">
    <div v-if="!requestIsInitiated" class="pathway-modal-content">
      <h2 class="text-center modal-header">{{ modalTitle }}</h2>
      <button
        class="btn btn-close-sm btn-position"
        @click="closeModal(false)"
        type="button"
      ></button>
      <p>
        Notify your team to complete this
        {{ requestType === "pathway" ? "pathway" : "resource" }} by using the date-picker below.
      </p>
      <p>Click 'Send' and all team members will be notified.</p>

      <div class="notification-text">{{ notificationText }}</div>
      <JyreDatePicker v-model="date" class="date-picker" :limit-from="today" />
      <div class="button-container">
        <button class="btn btn-default-close" type="button" @click="closeModal(false)">
          Cancel
        </button>
        <button
          class="btn btn-primary-communication"
          type="button"
          @click="initiateRequest"
          :disabled="isSaving"
          data-test="level-send-requests-button"
        >
          Send
        </button>
      </div>
      <p v-if="errorMessage.length" class="text-danger">{{ errorMessage }}</p>
    </div>

    <!--  AFTER INITIATION  -->
    <div v-else class="pathway-modal-content">
      <h2 class="text-center modal-header">Sent!</h2>
      <p>
        {{ successText }}
      </p>
      <div class="lottie-container">
        <lottie-player style="width: 100px" src="/Lottie/tick.json" autoplay />
      </div>
      <button
        class="btn btn-primary"
        type="button"
        @click="() => closeModal(true)"
        data-test="level-confirm-request-button"
      >
        Close
      </button>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { Modal } from "uiv";
import { computed, inject, ref, Ref } from "vue";
import dayjs from "dayjs";
import useTeamLeadLevels, { LevelItem } from "../../composables/useTeamLeadLevels";
import cms from "../../services/cms/cms";
import JyreDatePicker from "../event-log/jyre-date-picker.vue";
import { csContext } from "../../services/cs-context";
import useNotification from "../../composables/useNotification";

const level = inject("selectedLevel") as Ref<LevelItem>;

const props = defineProps<{ modelValue: boolean }>();

const emit = defineEmits(["close-request-modal"]);

const isDisplayed = computed(() => {
  return props.modelValue;
});

const { castAsLevel, initiatePathwayFromLevel } = useTeamLeadLevels();
const { notifyTeamMembers } = useNotification();

const requestType = computed(() => {
  return castAsLevel(level.value.data).pathway_to_initiate ? "pathway" : "resource";
});

const modalTitle = computed(() => {
  return requestType.value === "pathway" ? "Initiate pathway" : "Notify team";
});

const notificationText = computed(() => {
  if (requestType.value === "resource") {
    return `${cms.asText(level.value.data.title)}`;
  }

  const pathwayTitle =
    cms.asText(castAsLevel(level.value?.data).pathway_to_initiate?.data?.title) ?? "";

  return `${pathwayTitle} Step 1 & 2`;
});

const successText = computed(() => {
  return requestType.value === "pathway"
    ? "You've successfully initiated the pathway and your team has been notified. You will be able to track their progress from the Team Lead area."
    : "You've successfully notified your team. You will be able to track their progress from the Team Lead area.";
});

const currentDate = dayjs().format("YYYY-MM-DD");
const date = ref(currentDate);
const today = dayjs(currentDate).format("YYYY-MM-DD");

const isSaving = ref(false);
const errorMessage = ref("");
const requestIsInitiated = ref(false);

function closeModal(triggerRefresh: boolean) {
  emit("close-request-modal", triggerRefresh);
}

async function initiatePathway() {
  isSaving.value = true;
  const teamId = csContext.currentTeam?.id;

  if (!teamId) return;

  const parsedLevel = castAsLevel(level.value.data);

  if (parsedLevel.initiate_pathway) {
    try {
      await initiatePathwayFromLevel(parsedLevel, teamId, date.value);

      if (parsedLevel.pathway_type === "personal") {
        await notifyTeamMembers(level.value, teamId, date.value);
      }

      requestIsInitiated.value = true;
      isSaving.value = false;
    } catch (error) {
      errorMessage.value = (error as Error).message;
    } finally {
      isSaving.value = false;
    }
  }
}

async function initiateRequest() {
  if (requestType.value === "pathway") {
    await initiatePathway();
  } else {
    const response = await notifyTeamMembers(level.value, csContext.currentTeam?.id, date.value);
    requestIsInitiated.value = true;
    if (response.error) {
      errorMessage.value = response.error;
    }
  }
}
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.pathway-modal-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;

  p {
    color: @form-text-color;
    margin-bottom: 0;
    text-align: center;
  }
}

.modal-header {
  margin: 0;
  border-bottom: none;
}

button.btn-position {
  position: absolute;
  top: 15px;
  right: 15px;
}

.notification-text {
  width: 100%;
  max-width: 400px;
  margin: auto;
  padding: 5px 20px;
  background-color: @charismatic-color;
  border-radius: 5px;
  color: white;
  font-weight: bold;
  font-size: 18px;
  text-align: center;
}

.button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;

  button.btn {
    width: 100px;
    height: 40px;
  }
}

.lottie-container {
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
